/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input,
  OnDestroy, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { CountriesVm } from '@booking/entities/countries';
import { User } from '@app/modules/core/interfaces/user';
import { BillingFacade } from '@billing/billing.facade';
import { BillingResponse, BillingCity, BillingVm, BillingInfo, BillingCreateItem } from '@billing/entities/billing.payload';
import { UserAddresses } from '@app/modules/core/interfaces/address';
import { AppFacade } from '@app/app.facade';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import {
  BOOKING_FORM_INPUTS, INVALID, CONTROL, BILLING_FORM_INPUTS, PERSON_FORM_INPUTS, PERSON_ADDRESS_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { PaxFacade } from '@pax/pax.facade';
import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { BookingFacade } from '@booking/booking.facade';
import { validAllFormSections } from '@booking/mappers/validate-form.mapper';
import { BookingBillingTypeDocumentProps, BusinessRulesByBookingType } from '@app/entities/booking-documents';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { BillingDocumentsPipe } from '@billing/pipes/billing-documents.pipe';
import { defaultBillingDocumentAbreviationByInfo, getBillingPersonType } from '@forms/mappers/form-document-input.mapper';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';
import { DataActionCardInfoType } from '@design-system/components/templates/data-action-card/entities/data-action-card.types';

@Component({
  selector: 'app-billing',
  templateUrl: './billing.container.html',
  styleUrls: ['./billing.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BillingContainer implements OnDestroy, OnInit {

  @Input() isSubmitted: boolean;
  @Input() countries: CountriesVm[] = [];
  @Input() cities: BillingCity[] = [];
  @Input() user: User;
  @Input() isMobile: boolean;
  @Input() bookingForm: UntypedFormGroup;
  @Input() byHotels: boolean;
  @Input() showHeaderInfo: boolean;

  public readonly icons = Icon;
  public readonly typographyFontSize = TypographyFontSize;
  public readonly typographyColor = TypographyColor;

  public readonly dataActionCardInfoType = DataActionCardInfoType;

  public showSwipeUpForm: boolean = false;
  public showSwipeUpOptions: boolean = false;
  public showBillingOptions: boolean = false;
  public defaultBilling: BillingResponse;

  public isInvalidContactInfo = false;

  private subscriptions: Subscription[] = [];
  private isCreateForm: boolean = false;

  private readonly BILLING_DATA_ERROR: string = 'BILLING_DATA_ERROR';

  protected AvoDispatch = new AvoDispatchOrchestrator();
  
  constructor(
    private appFacade: AppFacade,
    private domManipulateService: DomManipulateService,
    private formValidationService: FormValidationService,
    private facade: BillingFacade,
    private paxFacade: PaxFacade,
    private bookingFacade: BookingFacade,
    private billingDocumentsPipe: BillingDocumentsPipe,
    private readonly cdr: ChangeDetectorRef,
    private readonly avoService: AvoService,
  ) { }

  ngOnInit(): void {
    this.initContactInfoValidation();
    this.subscriptions.push(
      this.hasDefaultBilling$.subscribe
      ((hasDefaultBilling) => {
        this.bookingFacade.bookingReadyControl(validAllFormSections(this.bookingForm), true, hasDefaultBilling);
      }));
    this.subscriptions.push(
      this.defaultBilling$.subscribe(defaultBilling => {
        if (defaultBilling) {
          this.updateForm(defaultBilling.billing_info);
        }
      })
    );
    this.subscriptions.push(
      combineLatest([
        this.bookingForm.get(BOOKING_FORM_INPUTS.PHONE).valueChanges,
        this.bookingForm.get(BOOKING_FORM_INPUTS.PHONE).statusChanges,
        this.bookingForm.get(BOOKING_FORM_INPUTS.EMAIL).valueChanges,
        this.bookingForm.get(BOOKING_FORM_INPUTS.EMAIL).statusChanges,
      ]).pipe(
        tap(([phoneValue, phoneStatus, emailValue, emailStatus]) => {
          this.isInvalidContactInfo = this.getIsInvalidContactInfo(phoneValue, phoneStatus, emailValue, emailStatus);
          this.cdr.detectChanges();
        })
      ).subscribe()
    );
    this.facade.fetchDefaultBilling({ userId: this.user.id });
    this.facade.fetchUserBillings({ userId: this.user.id });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.facade.resetBillingModuleValues();
  }

  get firstPaxSelection$(): Observable<TravelerPaxResponse> {
    if (this.byHotels) {
      return this.paxFacade.guestListUI$.pipe(map(guestListUI => {
        if (guestListUI && guestListUI.length) {
          const defaultRoom = guestListUI[0];
          const roomGuests = defaultRoom.roomGuests;
          if (roomGuests && roomGuests.length) {
            const indexFirstAdult = roomGuests.findIndex(guest => guest.TravelerType === TravelerTypes.ADULTS);
            return indexFirstAdult >= 0 ? roomGuests[indexFirstAdult] && roomGuests[indexFirstAdult].defaultData ?
              roomGuests[indexFirstAdult].defaultData : null : null;
          }
        } else return null;
      }));
    } else {
      return this.paxFacade.passengerListUI$.pipe(map(passengerListUI => {
        if (passengerListUI && passengerListUI.length) {
          const indexFirstAdult = passengerListUI.findIndex(passenger => passenger.TravelerType === TravelerTypes.ADULTS);
          return indexFirstAdult >= 0 ? passengerListUI[indexFirstAdult] && passengerListUI[indexFirstAdult].defaultData ?
            passengerListUI[indexFirstAdult].defaultData : null : null;
        } else return null;
      }));
    }
  }

  get billingInfo$(): Observable<BillingResponse[]> {
    return this.facade.billingInfo$;
  }
  get userAddresses$(): Observable<UserAddresses> {
    return this.appFacade.userAddresses$;
  }
  get bookingType$(): Observable<TravelBookingType> {
    return this.facade.bookingType$;
  }

  get hasDefaultBilling$(): Observable<boolean> {
    return this.facade.hasDefaultBilling$;
  }

  get lastBillingUI$(): Observable<boolean> {
    return this.facade.lastBillingUI$;
  }
  get defaultBilling$(): Observable<BillingResponse> {
    return this.facade.defaultBillingInfo$;
  }

  get hasBillingError$(): Observable<boolean> {
    return this.hasDefaultBilling$.pipe(map(hasDefaultBilling => {
      if (this.isSubmitted) {
        return !hasDefaultBilling || !!this.isInvalidBillingForm;
      } else return !!hasDefaultBilling && !!this.isInvalidBillingForm;
    }));
  }

  get billingErrorMessage$(): Observable<string> {
    return this.hasDefaultBilling$.pipe(
      map(hasDefaultBilling => {
        if (this.isInvalidContactInfo && !hasDefaultBilling) return 'MANDATORY_BILLING_CONTACT_SECTION';
        if (this.isSubmitted && !hasDefaultBilling) return 'MANDATORY_BILLING';
        else if (hasDefaultBilling && this.isInvalidBillingForm) return this.BILLING_DATA_ERROR;
        else return null;
      })
    );
  }

  get withErrorLaunch$(): Observable<boolean> {
    return this.billingErrorMessage$.pipe(map(message => !this.isCreateForm && message === this.BILLING_DATA_ERROR));
  }

  get businessRules$(): Observable<BusinessRulesByBookingType> {
    return combineLatest([this.appFacade.documentAssets$, this.bookingType$]).pipe(
      map(([_ENV, bookingType]) => _ENV?.BOOKING_MODULES_BUSINESS_RULES && _ENV.BOOKING_MODULES_BUSINESS_RULES[bookingType] ?
        _ENV.BOOKING_MODULES_BUSINESS_RULES[bookingType] : {} as BusinessRulesByBookingType)
    );
  }

  private get isInvalidBillingForm(): boolean {
    return this.bookingForm?.get(BOOKING_FORM_INPUTS.BILLING).status === INVALID;
  }

  public close(): void {
    this.toggleForm();
  }

  public documentDefaultBilling$(defaultBilling: BillingResponse): Observable<string> {
    if (defaultBilling?.billing_info?.configuration_document_id) {
      const type = getBillingPersonType(!!defaultBilling.billing_info.natural_person);
      const country = defaultBilling.billing_info.address?.country_code;
      return this.billingDocumentsPipe.transform(type, country).pipe(
        map(documents => defaultBillingDocumentAbreviationByInfo(documents, defaultBilling))
      );
    } else return of(null);
  }

  public toggleForm(isCreating: boolean = true): void {
    this.isCreateForm = isCreating;
    this.showSwipeUpForm = !this.showSwipeUpForm;
    if (isCreating && this.showSwipeUpForm) {
      this.defaultBilling = null;
    }
    if (this.isMobile) {
      this.domManipulateService.setBodyScroll(!this.showSwipeUpForm);
    } else {
      if (this.showBillingOptions) { 
        this.showBillingOptions = false;
      }
      this.domManipulateService.setBodyScroll(!this.showSwipeUpForm);
    }
    if (this.showSwipeUpForm) {
      this.avoService.getEventTriggerProps$()
        .pipe(tap(trigger => {
          this.AvoDispatch.viewInvoicingForm({
            ...this.avoService.avoBaseProperties(trigger),
          })
        })).subscribe().unsubscribe();
    }
  }

  public toggleOptions(): void {
    if (this.isMobile) {
      this.showSwipeUpOptions = !this.showSwipeUpOptions;
      this.domManipulateService.setBodyScroll(!this.showSwipeUpOptions);
    } else {
      this.showBillingOptions = !this.showBillingOptions;
      this.domManipulateService.setBodyScroll(!this.showBillingOptions);
    }
  }

  public doubleToggle(): void {
    this.toggleOptions();
    this.toggleForm();
  }

  public deleteOption(billingId: string): void {
    this.facade.defaultBillingInfo$.pipe(
      tap((defaultBillingInfo) => {
        if (billingId !== defaultBillingInfo?._id) {
          this.facade.deleteBilling({ billingId });
          this.toggleOptions();
        }
      })
    ).subscribe().unsubscribe();
  }

  public createBillingInfo(billingCreateForm: BillingCreateItem): void {
    this.facade.createBilling(billingCreateForm);
    this.updateForm(billingCreateForm.payload.billing_info);
    this.toggleForm();
  }

  public updateBillingInfo(billingVm: BillingVm): void {
    this.defaultBilling = {
      _id: billingVm.billingId,
      billing_info: billingVm.billingInfo.billing_info,
      user_id: billingVm.userId
    };
    this.facade.setBilling(billingVm);
    this.updateForm(billingVm.billingInfo.billing_info);
    this.toggleForm(false);
  }

  public selectBillingOption(billing: BillingResponse): void {
    this.defaultBilling = billing;
    this.facade.setDefaultBillingData(billing);
    this.toggleOptions();
  }

  public updateBillingOption(billing: BillingResponse): void {
    this.defaultBilling = billing;
    this.toggleOptions();
    this.toggleForm(false);
  }

  private updateForm(billing: BillingInfo): void {
    const formBilling = this.bookingForm.get(BOOKING_FORM_INPUTS.BILLING).get(CONTROL);
    if (formBilling) {
      formBilling.patchValue({ [BILLING_FORM_INPUTS.BILLING_INFO]: billing });
      const billingControl: UntypedFormGroup = formBilling.get(BILLING_FORM_INPUTS.BILLING_INFO) as UntypedFormGroup;
      this.setDocumentValidator(billingControl);
      this.setNameValidator(billingControl);
      this.cdr.detectChanges();
    }
  }

  private setDocumentValidator(formBilling: UntypedFormGroup): void {
    if (formBilling) {
      const value = formBilling.value;
      const isNaturalPerson = value[PERSON_FORM_INPUTS.NATURAL_PERSON];
      const documentId = value[PERSON_FORM_INPUTS.DOCUMENT_ID];
      const country = value[PERSON_FORM_INPUTS.ADDRESS][PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE];
      this.getDocumentByFormUpdate$(isNaturalPerson, country, documentId).pipe(
        take(1),
        tap(document => {
          this.formValidationService.setBillingDocumentValidators(formBilling, document.TYPE, document.VALIDATIONS);
        })
      ).subscribe().unsubscribe();
    }
  }

  private setNameValidator(formBilling: UntypedFormGroup): void {
    if (formBilling) {
      const value = formBilling.value;
      const isNaturalPerson = value[PERSON_FORM_INPUTS.NATURAL_PERSON];
      if (isNaturalPerson) {
        formBilling.get(PERSON_FORM_INPUTS.LEGAL_NAME).setValidators([...this.formValidationService.stringValidators]);
      } else {
        formBilling.get(PERSON_FORM_INPUTS.LEGAL_NAME).setValidators([Validators.minLength(2),
          Validators.maxLength(80), Validators.required]);
      }
      formBilling.get(PERSON_FORM_INPUTS.LEGAL_NAME).updateValueAndValidity();
    }
  }

  private getDocumentByFormUpdate$(
    isNaturalPerson: boolean, country: string, documentId: number
  ): Observable<BookingBillingTypeDocumentProps> {
    const type = getBillingPersonType(isNaturalPerson);
    return this.billingDocumentsPipe.transform(type, country).pipe(
      map(documents => documents.find(document => document.DOCUMENT_ID === documentId))
    );
  }

  private getIsInvalidContactInfo(phoneValue: string, phoneStatus: string, emailValue: string, emailStatus: string): boolean {
    return emailStatus === INVALID || phoneStatus === INVALID || !emailValue || !phoneValue;
  }

  private initContactInfoValidation(): void {
    const email = this.bookingForm.get(BOOKING_FORM_INPUTS.EMAIL);
    const phone = this.bookingForm.get(BOOKING_FORM_INPUTS.PHONE);
    this.isInvalidContactInfo = this.getIsInvalidContactInfo(phone.value, phone.status, email.value, email.status);
    this.cdr.detectChanges();
  }
}
